import { startRegistration } from "@simplewebauthn/browser";
import { THEME_STORAGE_KEY } from "@szyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { STUDIO_BUILD, api, errorMessage, post, type AuthStatus } from "../api";
import { timeSec, usd } from "../format";
import { Icon } from "../icons";
import { useLive } from "../live";
import { useToast } from "./feedback";

const NAV = [
  { to: "/", icon: "home", label: "Cockpit", exact: true },
  { to: "/ideas", icon: "idea", label: "Ideen", badge: "ideas" },
  { to: "/projects", icon: "folder", label: "Projekte" },
  { to: "/decisions", icon: "inbox", label: "Entscheid.", badge: "decisions" },
  { to: "/agents", icon: "bot", label: "Agenten" },
  // Auf dem Handy über die Kachel im Cockpit erreichbar; die untere Leiste zeigt nur fünf Einträge.
  { to: "/usage", icon: "activity", label: "Verbrauch" },
] as const;

/** Bildmarke: Unendlich-Schleife vom Funken (Punkt) zum Ergebnis (Haken). */
export function BrandMark() {
  const grad = `mark${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg className="brand-svg" viewBox="6 6 108 48" aria-hidden="true">
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3fd8ff" />
          <stop offset=".55" stopColor="#3a8bff" />
          <stop offset="1" stopColor="#a35cff" />
        </linearGradient>
      </defs>
      <g fill="none" stroke={`url(#${grad})`} strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M44 16A18.4 18.4 0 1 0 44 44L76 16A18.4 18.4 0 1 1 76 44L67.5 36.5" />
        <path d="M82 30.5l4.5 4.5 8.5-8.5" />
      </g>
      <path
        d="M39.4 19.6L47.4 10.4L50.6 23.4Z"
        fill={`url(#${grad})`}
        stroke={`url(#${grad})`}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="30" r="5.5" fill={`url(#${grad})`} />
    </svg>
  );
}

export function passkeyError(err: unknown): string {
  if (err instanceof Error && err.name === "NotAllowedError")
    return "Passkey-Dialog abgebrochen oder abgelaufen.";
  if (err instanceof Error && err.name === "InvalidStateError")
    return "Dieses Gerät hat bereits einen Passkey für szyOS.";
  return errorMessage(err);
}

/** Weiteren Passkey für den angemeldeten Menschen anlegen, z. B. auf dem Handy per QR-Code. */
async function addPasskey(): Promise<void> {
  const { challengeId, options } = await post<{
    challengeId: string;
    options: Parameters<typeof startRegistration>[0]["optionsJSON"];
  }>("/api/v1/auth/passkeys/options");
  const response = await startRegistration({ optionsJSON: options });
  await post("/api/v1/auth/passkeys/verify", { challengeId, response });
}

export function Logo() {
  return (
    <div className="logo">
      <BrandMark />
      <span className="logo-word">
        <span className="logo-szy">szy</span>
        <span className="logo-os">OS</span>
      </span>
      <span className="logo-claim">
        Spark to <span className="logo-claim-done">Done.</span>
      </span>
    </div>
  );
}

function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") ?? "dark");
  const toggle = useCallback(() => {
    const next = theme === "light" ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* Speicher nicht verfügbar: Theme gilt nur für diese Sitzung */
    }
    setTheme(next);
  }, [theme]);
  return [theme, toggle];
}

interface Command {
  group: string;
  label: string;
  icon: string;
  hint?: string;
  danger?: boolean;
  run: () => void;
}

function CommandPalette({
  open,
  onClose,
  commands,
}: {
  open: boolean;
  onClose: () => void;
  commands: Command[];
}) {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const matches = useMemo(
    () => commands.filter((c) => c.label.toLowerCase().includes(query.trim().toLowerCase())),
    [commands, query],
  );
  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  if (!open) return null;
  const run = (c: Command | undefined) => {
    if (!c) return;
    onClose();
    c.run();
  };
  let group = "";
  return (
    <div
      className="palette-wrap"
      role="dialog"
      aria-modal="true"
      aria-label="Befehlspalette"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="palette">
        <div className="palette-input">
          <Icon name="search" />
          <input
            ref={input}
            value={query}
            placeholder="Befehl oder Ziel eingeben …"
            aria-label="Befehl suchen"
            aria-controls="paletteList"
            aria-activedescendant={matches.length ? `cmd-${index}` : undefined}
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setIndex((i) => (i + 1) % Math.max(1, matches.length));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setIndex((i) => (i - 1 + matches.length) % Math.max(1, matches.length));
              } else if (e.key === "Enter") {
                e.preventDefault();
                run(matches[index]);
              } else if (e.key === "Escape") {
                onClose();
              } else if (e.key === "Tab") {
                e.preventDefault();
              }
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <ul className="palette-list" id="paletteList" role="listbox" aria-label="Befehle">
          {matches.length === 0 && <li className="palette-empty">Kein Treffer</li>}
          {matches.map((c, i) => {
            const header = c.group !== group ? ((group = c.group), c.group) : null;
            return (
              <li key={`${c.group}-${c.label}`} role="presentation">
                {header && <div className="palette-group">{header}</div>}
                <div
                  id={`cmd-${i}`}
                  role="option"
                  aria-selected={i === index}
                  className={`palette-item${c.danger ? " is-danger" : ""}`}
                  onMouseMove={() => setIndex(i)}
                  onClick={() => run(c)}
                >
                  <Icon name={c.icon} />
                  <span>{c.label}</span>
                  {c.hint && <span className="hint">{c.hint}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function Shell({ auth, children }: { auth: AuthStatus; children: ReactNode }) {
  const live = useLive();
  // Neue Version auf dem Server: Hinweis zeigen, statt mit altem Stand weiterzuarbeiten (QA-06).
  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const outdated =
    STUDIO_BUILD !== "dev" &&
    Boolean(health.data?.build) &&
    health.data?.build !== "dev" &&
    health.data?.build !== STUDIO_BUILD;
  const [theme, toggleTheme] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const decisions = useQuery({ queryKey: ["decisions"], queryFn: () => api.decisions("PENDING") });
  const ideas = useQuery({ queryKey: ["ideas"], queryFn: api.ideas });
  const costs = useQuery({ queryKey: ["costs"], queryFn: api.costsToday });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  const isOwner = auth.user?.role === "owner";

  const kill = useMutation({
    mutationFn: () => api.triggerKillSwitch("Über die Befehlspalette ausgelöst"),
    onSuccess: (r) => toast(`Kill Switch aktiv: ${r.cancelledRuns} Läufe gestoppt, Leases widerrufen.`),
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const passkey = useMutation({
    mutationFn: addPasskey,
    onSuccess: () => toast("Weiterer Passkey gespeichert. Du kannst dich jetzt auch damit anmelden."),
    onError: (err) => toast(passkeyError(err), "error"),
  });
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => queryClient.invalidateQueries(),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const badges: Record<string, number> = {
    decisions: decisions.data?.length ?? 0,
    ideas: ideas.data?.filter((i) => i.maturity === "RAW").length ?? 0,
  };
  const costTotal = (costs.data ?? []).reduce((s, r) => s + r.measuredUsd + r.estimatedUsd, 0);
  const runsToday = (costs.data ?? []).reduce((s, r) => s + r.runs, 0);

  const commands: Command[] = [
    {
      group: "Aktionen",
      label: "Neue Idee erfassen",
      icon: "idea",
      hint: "Ideen",
      run: () => void navigate({ to: "/ideas", search: { neu: "1" } }),
    },
    {
      group: "Aktionen",
      label: "Neues Projekt anlegen",
      icon: "plus",
      run: () => void navigate({ to: "/projects", search: { neu: "1" } }),
    },
    { group: "Navigation", label: "Cockpit", icon: "home", run: () => void navigate({ to: "/" }) },
    {
      group: "Navigation",
      label: "Entscheidungen öffnen",
      icon: "inbox",
      hint: `${badges.decisions} offen`,
      run: () => void navigate({ to: "/decisions" }),
    },
    {
      group: "Navigation",
      label: "Token-Verbrauch",
      icon: "activity",
      run: () => void navigate({ to: "/usage" }),
    },
    {
      group: "Navigation",
      label: "Agenten und Nodes",
      icon: "bot",
      run: () => void navigate({ to: "/agents" }),
    },
    ...(projects.data ?? []).map((p) => ({
      group: "Projekte",
      label: `Projekt: ${p.title}`,
      icon: "folder",
      run: () => void navigate({ to: "/projects/$projectId", params: { projectId: p.id } }),
    })),
    ...(workOrders.data ?? []).slice(0, 8).map((w) => ({
      group: "Aufträge",
      label: `Auftrag: ${w.title} (${w.projectTitle})`,
      icon: "network",
      run: () => void navigate({ to: "/work-orders/$orderId", params: { orderId: w.id } }),
    })),
    {
      group: "Darstellung",
      label: theme === "light" ? "Dunklen Modus einschalten" : "Hellen Modus einschalten",
      icon: theme === "light" ? "moon" : "sun",
      run: toggleTheme,
    },
    ...(isOwner
      ? [
          {
            group: "Sicherheit",
            label: "Kill Switch: alle Läufe stoppen",
            icon: "power",
            hint: "nur Owner",
            danger: true,
            run: () => kill.mutate(),
          },
        ]
      : []),
    {
      group: "Sitzung",
      label: "Weiteren Passkey hinzufügen (z. B. Handy)",
      icon: "key",
      run: () => passkey.mutate(),
    },
    { group: "Sitzung", label: "Abmelden", icon: "logout", run: () => logout.mutate() },
  ];

  return (
    <div className="app">
      <nav className="sidebar" aria-label="Hauptnavigation">
        <Link to="/" className="brand-mark" aria-label="szyOS Cockpit">
          <BrandMark />
        </Link>
        <ul className="nav-list">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                className="nav-item"
                activeProps={{ className: "nav-item is-active", "aria-current": "page" }}
                activeOptions={{ exact: "exact" in n ? n.exact : false }}
              >
                <Icon name={n.icon} />
                <span>{n.label}</span>
                {"badge" in n && badges[n.badge] ? (
                  <b
                    className={`nav-badge${n.badge === "decisions" ? " nav-badge--amber" : ""}`}
                    aria-label={`${badges[n.badge]} offen`}
                  >
                    {badges[n.badge]}
                  </b>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="nav-item nav-item--bottom"
          onClick={() => logout.mutate()}
          aria-label="Abmelden"
        >
          <Icon name="logout" />
          <span>Abmelden</span>
        </button>
      </nav>

      <main id="main" className="main">
        {outdated && (
          <div className="update-banner" role="alert">
            <Icon name="refresh" />
            <span>
              <strong>Neue Version von szyOS verfügbar.</strong> Bitte neu laden, damit nichts mit dem alten
              Stand gespeichert wird.
            </span>
            <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
              Jetzt neu laden
            </button>
          </div>
        )}
        <header className="topbar">
          <Logo />
          <button
            className="search"
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-haspopup="dialog"
            aria-keyshortcuts="Control+K"
          >
            <Icon name="search" />
            <span>Projekt, Auftrag, Entscheidung oder Befehl …</span>
            <kbd>Strg</kbd>
            <kbd>K</kbd>
          </button>
          <div className="topbar-right">
            <button
              type="button"
              className="theme-toggle"
              aria-label="Weiteren Passkey hinzufügen, z. B. für das Handy"
              title="Weiteren Passkey hinzufügen"
              disabled={passkey.isPending}
              onClick={() => passkey.mutate()}
            >
              <Icon name="key" />
            </button>
            <button
              type="button"
              className="theme-toggle"
              aria-pressed={theme === "light"}
              aria-label={theme === "light" ? "Dunklen Modus einschalten" : "Hellen Modus einschalten"}
              onClick={toggleTheme}
            >
              <Icon name={theme === "light" ? "moon" : "sun"} />
            </button>
            <div className={`sys-status${live.connected ? "" : " is-paused"}`} role="status">
              <span className="pulse-dot" aria-hidden="true" />
              <span>
                <strong>{live.connected ? "Live verbunden" : "Live pausiert"}</strong>
                <small>
                  {live.lastConfirmedAt ? `Stand ${timeSec(live.lastConfirmedAt)}` : "verbinde …"} ·{" "}
                  {auth.user?.displayName}
                </small>
              </span>
            </div>
            <div className="kpi" title="Gemessene und geschätzte API-Kosten heute, ohne Abo-Kontingente">
              <span className="kpi-value">{usd(costTotal)}</span>
              <span className="kpi-label">Kosten heute</span>
              <span className="kpi-bars" aria-hidden="true">
                {[30, 45, 38, 60, 52, 74, 68, 90].map((h, i) => (
                  <i key={i} style={{ ["--h" as string]: `${h}%` }} />
                ))}
              </span>
            </div>
            <div className="kpi" title="Agentenläufe heute">
              <span className="kpi-value">{runsToday}</span>
              <span className="kpi-label">Läufe heute</span>
              <span className="kpi-bars kpi-bars--lime" aria-hidden="true">
                {[60, 80, 70, 95, 85, 100, 70, 100].map((h, i) => (
                  <i key={i} style={{ ["--h" as string]: `${h}%` }} />
                ))}
              </span>
            </div>
          </div>
        </header>
        {children}
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={commands} />
    </div>
  );
}
